import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import registrationService from "../../utils/registrationService";
import { useAuth } from "../../contexts/AuthContext";
import Header from "../../components/ui/Header";
import ProgressHeader from "../../components/ui/ProgressHeader";
import PersonalInfoStep from "./components/PersonalInfoStep";
import AcademicBackgroundStep from "./components/AcademicBackgroundStep";
import DocumentUploadStep from "./components/DocumentUploadStep";
import PaymentStep from "./components/PaymentStep";
import FinalConfirmationStep from "./components/FinalConfirmationStep";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import ServiceErrorAlert from "../../components/ui/ServiceErrorAlert";

const MultiStepRegistrationForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();

  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [registrationId, setRegistrationId] = useState(null);

  // Pass current step to ProgressHeader via localStorage for synchronization
  useEffect(() => {
    localStorage.setItem("currentRegistrationStep", currentStep?.toString());
  }, [currentStep]);

  // Get selected profile from localStorage (enhanced bug fix)
  const getSelectedProfile = () => {
    // First try to get from localStorage (primary source)
    const savedProfile = localStorage.getItem("selectedProfile");
    if (savedProfile) {
      return savedProfile;
    }

    // Try to get from profileData if selectedProfile is missing
    const savedProfileData = localStorage.getItem("profileData");
    if (savedProfileData) {
      try {
        const profileData = JSON.parse(savedProfileData);
        return profileData?.id;
      } catch (error) {
        console.error("Error parsing saved profile data:", error);
      }
    }

    // Fallback to location state if available
    if (location?.state?.selectedProfile) {
      return location?.state?.selectedProfile;
    }

    // Last resort fallback
    return "software-developer";
  };

  const selectedProfile = getSelectedProfile();

  // Get profile info based on selected profile
  const getProfileInfo = () => {
    const profiles = {
      "fullstack-developer": {
        title: "Full Stack Web Developer",
        description: "Full Stack Web App Development Training",
        fee: "Tech Foundry: ₹45,000\nTech Elevate: ₹80,000",
      },
      "data-analytics": {
        title: "Data Analyst",
        description: "Data Analytics & Visualization Training",
        fee: "Tech Mastery: ₹90,000",
      },
      "it-support": {
        title: "IT Support Engineer",
        description: "IT Support professional Training",
        fee: "₹25,000",
      },
      "mobile-app-developer": {
        title: "Mobile App Developer",
        description: "Mobile App Development Training",
        fee: "₹60,000",
      },
      "devops": {
        title: "DevOps Engineer",
        description: "Cloud & DevOps Training",
        fee: "₹70,000",
      },
      "artificial-intelligence": {
        title: "AI Engineer",
        description: "Artificial Intelligence & Machine Learning Training",
        fee: "Tech Mastery: ₹95,000",
      },
    };

    return (
      profiles[selectedProfile] || {
        title: "Training Program",
        description: "Professional Training Program",
        fee: "₹40,000",
      }
    );
  };

  const profileInfo = getProfileInfo();

  const [formData, setFormData] = useState(() => {
    // Try to load saved data from localStorage
    const savedData = localStorage.getItem("registrationFormData");
    const currentSelectedProfile = getSelectedProfile(); // Ensure we get the latest profile

    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        // Force selectedProfile to be current selection (fix persistent wrong profile bug)
        const fixedData = {
          ...parsedData,
          selectedProfile: currentSelectedProfile,
          id: registrationId || parsedData?.id || null, // Preserve existing ID if any
        };
        // Update localStorage with corrected profile
        localStorage.setItem("registrationFormData", JSON.stringify(fixedData));
        return fixedData;
      } catch (error) {
        console.error("Error parsing saved form data:", error);
      }
    }

    return {
      id: registrationId,
      selectedProfile: currentSelectedProfile,
      // Personal Information
      fullName: "",
      dateOfBirth: "",
      gender: "",
      email: "",
      mobileNumber: "",
      whatsappNumber: "",
      otp: "",
      aadharNumber: "",
      fatherName: "",
      motherName: "",
      address: "",
      city: "",
      state: "",
      pincode: "",

      // Academic Background
      qualification: "",
      graduationYear: "",
      collegeName: "",
      technicalSkills: [],
      hasWorkExperience: false,
      experienceYears: "",
      previousCompany: "",
      previousRole: "",
      additionalInfo: "",

      // Document Uploads
      idProof: null,
      photo: null,
      resume: null,

      // Preferences
      trainingMode: "",
      placementCities: [],
      termsAccepted: false,
      privacyAccepted: false,
      feeStructureAccepted: false,
      communicationConsent: false,
    };
  });

  const totalSteps = 2;

  // Load existing registration data if user is authenticated
  useEffect(() => {
    if (user && !authLoading) {
      const loadExistingRegistration = async () => {
        try {
          const result = await registrationService?.getUserRegistration();
          if (result?.success && result?.data) {
            const existingData = result?.data;
            setRegistrationId(existingData?.id);

            // Map database data back to form structure
            const mappedData = {
              id: existingData?.id, 
              selectedProfile: existingData?.selected_profile,
              fullName: existingData?.full_name || "",
              dateOfBirth: existingData?.date_of_birth || "",
              gender: existingData?.gender || "",
              email: existingData?.email || "",
              mobileNumber: existingData?.mobile_number || "",
              whatsappNumber: existingData?.whatsapp_number || "",
              aadharNumber: existingData?.aadhar_number || "",
              fatherName: existingData?.father_name || "",
              motherName: existingData?.mother_name || "",
              address: existingData?.address || "",
              city: existingData?.city || "",
              state: existingData?.state || "",
              pincode: existingData?.pincode || "",
              qualification: existingData?.qualification || "",
              graduationYear: existingData?.graduation_year?.toString() || "",
              collegeName: existingData?.college_name || "",
              technicalSkills: existingData?.technical_skills || [],
              hasWorkExperience: existingData?.has_work_experience || false,
              experienceYears: existingData?.experience_years?.toString() || "",
              previousCompany: existingData?.previous_company || "",
              previousRole: existingData?.previous_role || "",
              additionalInfo: existingData?.additional_info || "",
              trainingMode: existingData?.training_mode || "",
              placementCities: existingData?.placement_cities || [],
              termsAccepted: existingData?.terms_accepted || false,
              privacyAccepted: existingData?.privacy_accepted || false,
              feeStructureAccepted:
                existingData?.fee_structure_accepted || false,
              communicationConsent:
                existingData?.communication_consent || false,
              // Keep document references as-is since they're handled separately
              idProof: formData?.idProof,
              photo: formData?.photo,
              resume: formData?.resume,
            };

            setFormData(mappedData);
          }
        } catch (error) {
          console.log("Error loading existing registration:", error);
        }
      };

      loadExistingRegistration();
    }
  }, [user, authLoading]);

  // Auto-save form data to both localStorage and Supabase (enhanced for guests)
  useEffect(() => {
    const saveData = async () => {
      try {
        // Always save to localStorage
        localStorage.setItem("registrationFormData", JSON.stringify(formData));

        // Only proceed if basic info is available
        if (formData?.email && formData?.fullName && formData?.mobileNumber) {
          // If no auth session, create guest account
          const user = await registrationService.getCurrentUser();
          if (!user) {
            await registrationService.createGuestAccount(formData);
          }

          // Save registration (works for both guest & authenticated users now)
          const result = await registrationService.saveRegistration(formData);
          if (result?.success) {
            const regId =
              result?.data?.id === "guest-registration"
                ? null
                : result?.data?.id;
            setRegistrationId(regId);
          }
        }

        setLastSaved(new Date());
      } catch (error) {
        console.error("Error saving form data:", error);
      }
    };

    const timeoutId = setTimeout(saveData, 2000);
    return () => clearTimeout(timeoutId);
  }, [formData]);

  // Load existing registration data (enhanced for guest users)
  useEffect(() => {
    const loadExistingRegistration = async () => {
      try {
        const result = await registrationService?.getUserRegistration();
        if (result?.success && result?.data) {
          const existingData = result?.data;

          // Handle both authenticated and guest data
          if (existingData?.isGuest) {
            // Guest data structure (from localStorage)
            setFormData((prev) => ({ ...prev, ...existingData }));
          } else {
            // Authenticated user data structure (from database)
            setRegistrationId(existingData?.id);

            // Map database data back to form structure
            const mappedData = {
              id: existingData?.id,
              selectedProfile: existingData?.selected_profile,
              fullName: existingData?.full_name || "",
              dateOfBirth: existingData?.date_of_birth || "",
              gender: existingData?.gender || "",
              email: existingData?.email || "",
              mobileNumber: existingData?.mobile_number || "",
              whatsappNumber: existingData?.whatsapp_number || "",
              aadharNumber: existingData?.aadhar_number || "",
              fatherName: existingData?.father_name || "",
              motherName: existingData?.mother_name || "",
              address: existingData?.address || "",
              city: existingData?.city || "",
              state: existingData?.state || "",
              pincode: existingData?.pincode || "",
              qualification: existingData?.qualification || "",
              graduationYear: existingData?.graduation_year?.toString() || "",
              collegeName: existingData?.college_name || "",
              technicalSkills: existingData?.technical_skills || [],
              hasWorkExperience: existingData?.has_work_experience || false,
              experienceYears: existingData?.experience_years?.toString() || "",
              previousCompany: existingData?.previous_company || "",
              previousRole: existingData?.previous_role || "",
              additionalInfo: existingData?.additional_info || "",
              trainingMode: existingData?.training_mode || "",
              placementCities: existingData?.placement_cities || [],
              termsAccepted: existingData?.terms_accepted || false,
              privacyAccepted: existingData?.privacy_accepted || false,
              feeStructureAccepted:
                existingData?.fee_structure_accepted || false,
              communicationConsent:
                existingData?.communication_consent || false,
              // Keep document references as-is since they're handled separately
              idProof: formData?.idProof,
              photo: formData?.photo,
              resume: formData?.resume,
            };

            setFormData(mappedData);
          }
        }
      } catch (error) {
        console.log("Error loading existing registration:", error);
      }
    };

    // Load registration data regardless of authentication status
    loadExistingRegistration();
  }, []);

  // Redirect if no profile selected
  useEffect(() => {
    if (!selectedProfile) {
      navigate("/profile-selection-landing");
    } else {
      // Ensure formData always has the latest selectedProfile
      setFormData((prev) => ({
        ...prev,
        selectedProfile: selectedProfile,
      }));
    }
  }, [selectedProfile, navigate]);

  const updateFormData = (newData) => {
    setFormData((prev) => ({ ...prev, ...newData }));
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep((prev) => prev + 1);
      setErrors({});
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      // handleSubmit();
      navigate("/document-upload-management", { state: { registrationId } });
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      setErrors({});
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      // Go back to profile selection
      navigate("/profile-selection-landing");
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setErrors({});

    try {
      console.log("Form Data:", formData);
      console.log("Next Step clicked");
      console.log("OTP Verified:", formData?.otpVerified);
      console.log("OTP Sent:", formData?.otpSent);
      console.log("Phone Verified:", formData?.phoneVerified);

      // Add error boundary for form processing
      const processFormSubmission = async () => {
        // Submit registration to Supabase with enhanced error handling
        const result = await registrationService?.submitRegistration(formData);

        if (!result?.success) {
          // Handle specific error cases
          if (result?.code === "AUTH_REQUIRED" && result?.fallbackData) {
            // Show user that registration is saved but needs auth resolution
            setErrors({
              submit: result?.error,
              fallback:
                "Your registration has been saved locally. Our support team will help complete the authentication process.",
              supportInfo:
                "Please contact support@solvarsity.com with your registration details.",
            });

            // Still navigate to success page with special state
            setTimeout(() => {
              navigate("/registration-success-confirmation", {
                state: {
                  registrationData: result?.fallbackData,
                  fromRegistration: true,
                  pendingAuth: true,
                  supportRequired: true,
                },
              });
            }, 3000);

            return result;
          }

          throw new Error(
            result?.error || "Failed to submit registration. Please try again."
          );
        }

        return result;
      };

      const result = await processFormSubmission();

      // Clear saved form data from localStorage since it's now submitted
      //localStorage.removeItem('registrationFormData');

      // Send registration emails
      try {
        const { data: functions } =
          await registrationService?.sendRegistrationEmails({
            registrationData: formData,
            paymentData: {
              payment_id: formData?.paymentId,
              order_id: formData?.orderId,
              amount: formData?.paymentAmount,
            },
            adminEmail: "solvarsity@solutioneers.in", // Fixed: hardcoded admin email
            userEmail: formData?.email,
          });
      } catch (error) {
        console.error("Error sending registration emails:", error);
      }

      // Navigate to success page
      navigate("/registration-success-confirmation", {
        state: {
          registrationData: result?.data,
          fromRegistration: true,
        },
      });
    } catch (error) {
      console.error("Registration submission error:", error);

      // Enhanced error handling with more specific messages
      let errorMessage = "Failed to submit registration. Please try again.";

      if (
        error?.message?.includes("Authentication required") ||
        error?.message?.includes("auth")
      ) {
        errorMessage =
          "Authentication issue detected. Your registration data has been saved locally.";
        supportActions = [
          "Try refreshing the page and submitting again",
          "Clear your browser cache and try again",
          "Contact support if the issue persists",
        ];
      } else if (
        error?.message?.includes("network") ||
        error?.message?.includes("fetch")
      ) {
        errorMessage =
          "Network error. Please check your internet connection and try again.";
      } else if (error?.message?.includes("auth")) {
        errorMessage =
          "Authentication error. Please verify your details and try again.";
        supportActions = [
          "Check your internet connection",
          "Try again in a few moments",
          "Your data has been saved locally",
        ];
      } else if (error?.message?.includes("FS is not a function")) {
        errorMessage =
          "Form processing error detected. Please refresh the page and try again.";
        supportActions = [
          "Refresh the browser page",
          "Clear browser cache",
          "Try submitting again",
        ];
      } else if (error?.message) {
        errorMessage = error?.message;
      }

      setErrors({
        submit: errorMessage,
        supportActions: supportActions,
        timestamp: new Date()?.toLocaleString(),
      });

      // Scroll to error message
      window.scrollTo({ top: document.body?.scrollHeight, behavior: "smooth" });
    } finally {
      setIsLoading(false);
    }
  };

  // Step labels for enhanced progress display
  const getStepTitle = () => {
    const stepTitles = {
      1: "Personal Information",
      2: "Academic Background",
      3: "Document Upload",
      4: "Payment",
      5: "Final Confirmation",
    };
    return stepTitles?.[currentStep] || "Registration";
  };

  const renderCurrentStep = () => {
    const stepProps = {
      data: formData,
      onUpdate: updateFormData,
      onNext: handleNext,
      onBack: handlePrevious,
      errors,
      setErrors,
      registrationId, // Pass registration ID for document uploads
      isLoading, // Pass loading state to components
    };

    switch (currentStep) {
      case 1:
        return <PersonalInfoStep {...stepProps} />;
      case 2:
        return <AcademicBackgroundStep {...stepProps} />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ProgressHeader />

      {/* Enhanced Step Progress Display */}
      <div className="w-full bg-surface border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                <span className="text-primary font-semibold text-sm">
                  {currentStep}
                </span>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  {getStepTitle()}
                </h2>
                <p className="text-sm text-text-secondary">
                  Step {currentStep} of {totalSteps}
                </p>
              </div>
            </div>

            <div className="hidden sm:flex items-center space-x-3">
              {/* <div className="text-right">
                <p className="text-sm text-text-secondary">Progress</p>
                <p className="text-lg font-bold text-primary">
                  {Math.round((currentStep / totalSteps) * 100)}%
                </p>
              </div> */}
              <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                {/* <div
                  className="h-full bg-primary transition-smooth duration-300"
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                /> */}
              </div>
            </div>
          </div>

          {/* Mobile progress bar */}
          {/* <div className="sm:hidden mt-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-text-secondary">Progress</span>
              <span className="font-medium text-primary">
                {Math.round((currentStep / totalSteps) * 100)}%
              </span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-smooth duration-300"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              />
            </div>
          </div> */}
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Header */}
        <div className="bg-surface rounded-lg border border-border p-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
                <Icon
                  name="Target"
                  size={24}
                  color="var(--color-primary)"
                  strokeWidth={2}
                />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-foreground">
                  {profileInfo?.title}
                </h1>
                <p className="text-m pt-3 text-text-secondary">
                  {profileInfo?.description}
                </p>
              </div>
            </div>

            <div className="text-right">
              <p className="text-sm text-text-secondary">Training Fee</p>
              <p className="text-xl pt-2 font-bold text-primary whitespace-pre-line">
                {profileInfo?.fee}
              </p>
            </div>
          </div>
        </div>

        {/* Auto-save Indicator */}
        {lastSaved && (
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-2 px-3 py-1 bg-success/10 border border-success/20 rounded-full">
              <Icon
                name="Check"
                size={14}
                color="var(--color-success)"
                strokeWidth={2}
              />
              <span className="text-xs text-success font-medium">
                Auto-saved at {lastSaved?.toLocaleTimeString()}
              </span>
            </div>
          </div>
        )}

        {/* Form Content */}
        <div className="bg-surface rounded-lg border border-border">
          <div className="p-6 sm:p-8">{renderCurrentStep()}</div>
        </div>

        {/* Enhanced Submit Error with Retry Options */}
        {errors?.submit && (
          <ServiceErrorAlert
            error={errors?.submit}
            supportActions={errors?.supportActions}
            fallback={errors?.fallback}
            onRetry={() => handleSubmit()}
            onDismiss={() => setErrors({})}
            showSupport={true}
          />
        )}

        {/* Help Section */}
        <div className="mt-8 bg-muted/50 rounded-lg border border-border p-6">
          <div className="flex items-start space-x-3">
            <Icon
              name="HelpCircle"
              size={20}
              color="var(--color-primary)"
              strokeWidth={2}
            />
            <div>
              <h3 className="text-base font-semibold text-foreground mb-2">
                Need Help?
              </h3>
              <p className="text-sm text-text-secondary mb-3">
                If you're having trouble with the registration process, our
                support team is here to help.
              </p>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Phone"
                  iconPosition="left"
                  onClick={() => window.open("tel:+919454588858", "_blank")}
                >
                  Call Support
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Mail"
                  iconPosition="left"
                  onClick={() =>
                    window.open("mailto:solvarsity@solutioneers.in", "_blank")
                  }
                >
                  Email Support
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MultiStepRegistrationForm;
